import { useQuery,UseQueryResult } from '@tanstack/react-query';
import axios from 'axios';

import { useIndexedDB } from '@/hooks/useIndexedDB';
import { useOrganization } from '@/hooks/useOrganization';
import { useUser } from '@/hooks/useUser';
import { restUrl } from '@/services/utils';

import { CommunicationChannelDataType } from './communicationChannel.types';

/**
 * Record shape for the `communicationChannels` store in IndexedDB. Invalidation is server-driven
 * (Deepstream) or same-tab (BroadcastChannel).
 */
export interface CommunicationChannelCacheRecord {
    userId: string;
    organizationId: string;
    data: CommunicationChannelDataType[];
}

const COMMUNICATION_CHANNELS_STORE = 'communicationChannels';

export const useCommunicationChannelData = (enabled: boolean): UseQueryResult<
    CommunicationChannelDataType[]
> => {
    const { user } = useUser();
    const { organization } = useOrganization();
    const cache = useIndexedDB<CommunicationChannelCacheRecord>(COMMUNICATION_CHANNELS_STORE);

    const userId = user && user.userId !== undefined ? String(user.userId) : undefined;
    const organizationId = organization && organization.id !== undefined ? String(organization.id) : undefined;

    return useQuery<CommunicationChannelDataType[]>({
        queryKey: ['communicationchannels', userId, organizationId],
        enabled: enabled && !!userId && !!organizationId,
        queryFn: async (): Promise<CommunicationChannelDataType[]> => {
            const scopedUserId = userId as string;
            const scopedOrgId = organizationId as string;
            const cacheKey = [scopedUserId, scopedOrgId];

            const cached = await cache.get(cacheKey);
            if (cached && Array.isArray(cached.data)) {
                return cached.data;
            }

            const response = await axios.get<CommunicationChannelDataType[]>(
                restUrl('communicationchannels'),
                {}
            );
            const list = response.data ?? [];
            await cache.add(cacheKey, {
                userId: scopedUserId,
                organizationId: scopedOrgId,
                data: list,
            });
            return list;
        },
    });
};
